import React, { memo, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import LoadingBouncyDots from "../ui/LoadingBouncyDots";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title
);

/**
 * MetricBarChart
 * Expects datasets in Chart.js format, with data as:
 *  - category x: array of numbers matching labels length (recommended)
 *  - or array of {x, y} points (works too)
 */
function MetricBarChart({
  height = 240,
  labels = [],
  datasets,
  xLabel,
  yLabel,
  yMin,
  yMax,
  yTickFormat,
  showLegend = false,
  showTooltip = false,
  showGrid = true,
  loading = false,
  loadingText = "",
  overlayClassName = "bg-white/40 backdrop-blur-[1px]",
  defaultBorderWidth = 0,
}) {
  const primary = getPrimaryColour();

  const finalDatasets = useMemo(() => {
    if (!datasets || !datasets.length) return [];
    return datasets.map((ds) => ({
      backgroundColor: ds.backgroundColor ?? primary,
      borderColor: ds.borderColor ?? primary,
      borderWidth: ds.borderWidth ?? defaultBorderWidth,
      ...ds,
    }));
  }, [datasets, primary, defaultBorderWidth]);

  const hasData = finalDatasets.some((ds) => (ds.data ?? []).length > 0);

  const data = useMemo(
    () => ({
      labels,
      datasets: finalDatasets,
    }),
    [labels, finalDatasets]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: showLegend },
        tooltip: { enabled: showTooltip },
        title: { display: false },
      },
      scales: {
        x: {
          grid: { color: showGrid ? "rgba(0,0,0,0.06)" : "transparent" },
          border: { color: "rgba(0,0,0,0.2)" },
          title: { display: !!xLabel, text: xLabel },
        },
        y: {
          grid: { color: showGrid ? "rgba(0,0,0,0.06)" : "transparent" },
          border: { color: "rgba(0,0,0,0.2)" },
          ticks: {
            callback: (v) => (yTickFormat ? yTickFormat(v) : v),
          },
          title: { display: !!yLabel, text: yLabel },
          ...(typeof yMin === "number" ? { min: yMin } : {}),
          ...(typeof yMax === "number" ? { max: yMax } : {}),
        },
      },
      animation: { duration: 600 },
    }),
    [showLegend, showTooltip, showGrid, xLabel, yLabel, yMin, yMax, yTickFormat]
  );

  return (
    <div className="relative" style={{ height }}>
      <Bar data={data} options={options} />
      {(loading || !hasData) && (
        <div
          className={`pointer-events-none absolute inset-0 flex items-center justify-center ${overlayClassName}`}
          aria-hidden="true"
        >
          <LoadingBouncyDots text={loadingText} />
        </div>
      )}
    </div>
  );
}

export default memo(MetricBarChart);
